import { FC, useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import MultipleSelect, {
  Option,
} from '../../PractitionerProfile/MultipleSelect/MultipleSelect';
import {
  PractitionerFormValue,
  PractitionerWeeklyTimeSlot,
} from '../PractitionerSchedule/PractitionerSchedule';
import CopyOperatory from 'pages/OfficeSchedulePage/DayTimeSlot/DefaultTimeView/CopyOperatory/CopyOperatory';
import { ReactComponent as WarningIcon } from 'assets/icons/warning.svg';
import { sortBy } from 'lodash';

interface PractitionerOperatorySectionProps {
  day: string;
}

const PractitionerOperatorySection: FC<PractitionerOperatorySectionProps> = ({
  day,
}) => {
  const { watch, setValue } = useFormContext<PractitionerFormValue>();
  const {
    operatories: selectedOperatories,
    advancedWeeklyTimeSlot: timeSlots,
  } = watch();

  const [isOverflow, setIsOverflow] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isAnimate, setIsAnimate] = useState(false);

  useEffect(() => {
    const position = dropdownRef.current!.getBoundingClientRect();
    const screenHeight = window.innerHeight;
    const paddingY = 16 * 2;
    const contentHeight = 21 * selectedOperatories.length;
    const copyPanelHeight = paddingY + contentHeight;
    const elementHeight = position.top + 26;
    const isOverflow = elementHeight + copyPanelHeight >= screenHeight;
    setIsOverflow(isOverflow);
    setIsAnimate(true);
    const timerId = setTimeout(() => {
      setIsAnimate(false);
    }, 2500);
    return () => {
      clearInterval(timerId);
    };
  }, [selectedOperatories.length]);

  return (
    <div className="flex items-center flex-1 pl-5.2 gap-x-3">
      <div className="w-24.8" ref={dropdownRef}>
        <Controller
          name="advancedWeeklyTimeSlot"
          render={({ field: { name, value, onChange } }) => {
            const timeSlot = value.find(
              (slot: PractitionerWeeklyTimeSlot) => slot.day === day
            )!;

            const isFieldInValid = timeSlot.operatories
              ? timeSlot.operatories.length === 0
              : true;

            return (
              <>
                <MultipleSelect
                  name={name}
                  isError={isFieldInValid}
                  controlStyles={{
                    height: '4rem',
                    ...(isAnimate &&
                      timeSlot.isAnimate && {
                        animation: 'pulseGlow 3s ease-in-out 1',
                      }),
                  }}
                  options={sortBy(selectedOperatories, 'label')}
                  placeholder="Select Operatory"
                  value={timeSlot.operatories ?? []}
                  onChange={(value) => {
                    const updatedTimeSlots = [...timeSlots];
                    const timeSlot = updatedTimeSlots.find(
                      (slot) => slot.day === day
                    )!;

                    timeSlot.operatories = value as Option[];

                    onChange(updatedTimeSlots);
                  }}
                  {...(isOverflow && {
                    menuStyle: { transform: 'translateY(calc(-100% - 4rem))' },
                  })}
                />
                {isFieldInValid && (
                  <div className={`flex items-center gap-x-1 mt-0.8`}>
                    <WarningIcon />
                    <span className="text-12 text-red-01">
                      This field is required
                    </span>
                  </div>
                )}
              </>
            );
          }}
        />
      </div>
      <CopyOperatory
        day={day}
        timeSlots={timeSlots}
        onCopy={(timeSlots) =>
          setValue('advancedWeeklyTimeSlot', timeSlots, {
            shouldValidate: true,
          })
        }
      />
    </div>
  );
};

export default PractitionerOperatorySection;
